<template>
  <section v-if="user">
    <section v-if="link.externalLink || link.customEvent">
      <div @click="openLink" class="sidebar__link">
        <img v-if="getMenuItemIcon(link)" class="sidebar__link-icon" :src="getMenuItemIcon(link)"
             alt=""/>
        <p>{{ link.text ? link.text : $t(`adminTitles.${link.name}`) }}</p>
      </div>
    </section>
    <section v-else>
      <router-link v-if="linkAvailable(link) && !link.featureLockerText" :to="link.path"
                   class="sidebar__link">
        <img class="sidebar__link-icon" :src="getMenuItemIcon(link)" alt=""/>
        <img class="sidebar__link-icon--active" :src="getMenuItemIcon(link, true)" alt=""/>
        <p>{{ link.text ? link.text : $t(`adminTitles.${link.name}`) }}</p>
        <div v-if="link.isNew" class="sidebar__link-new">NEW</div>
        <div v-if="badgeInfo" :class="['sidebar__link-badge', `sidebar__link-badge--${badgeInfo.type}`]">
          {{ badgeInfo.value }}
        </div>
        <!-- Clients Clara handed over and nobody has picked up yet, next to the usual count. -->
        <div v-if="asapCount" class="sidebar__link-badge sidebar__link-badge--asap" :title="$t('adminTitles.claraFollowUp.requests.asapBadge')">
          ASAP {{ asapCount }}
        </div>
      </router-link>
      <div v-else-if="link.featureLockerText" class="sidebar__link sidebar__link--disable">
        <img class="sidebar__link-icon" :src="getMenuItemIcon(link)" alt=""/>
        <p>{{ $t(`adminTitles.${link.name}`) }}</p>
        <div class="sidebar__link-locker">
          <BaseFeatureLocker :size="'s'" :color="'light'"
                             :tooltip-text="$t(link.featureLockerText)">
          </BaseFeatureLocker>
        </div>
      </div>
    </section>
  </section>
</template>
<script>
import {mapGetters} from 'vuex';
import {AUTH_LOGOUT} from '@/store/actions/auth';
import featureMixin from '@/mixins/featureMixin';
import licenseMixin from '@/mixins/licenseMixin';
import BaseFeatureLocker
  from '@/views/RenovaDashboard/components/ui-kit/Cards/BaseFeatureLocker';
import growthbook from '@/utils/growthbook';

export default {
  name: 'SideBarLink',
  components: {
    BaseFeatureLocker,
  },
  data() {
    return {
    };
  },
  mixins: [featureMixin, licenseMixin],
  emits: ['linkClick'],
  computed: {
    ...mapGetters(['user', 'ticketList', 'providerType', 'leadTotalCount', 'notificationBadges']),
    asapCount() {
      if (this.link.name !== 'tickets' || !this.$store.getters.claraFollowUpsEnabled) return 0;
      return this.notificationBadges.requestsAsap || 0;
    },
    badgeInfo() {
      const { name, badgeKey } = this.link;
      const b = this.notificationBadges;

      const countKeys = {
        tickets: b.messages,
        Claims: b.claims,
        Leads: b.leads,
        Tasks: b.tasks,
        Renewals: b.renewals,
        Payments: b.payments,
      };

      if (name in countKeys && countKeys[name]) {
        const raw = countKeys[name];
        if (typeof raw === 'boolean') {
          return { type: 'dot', value: '' };
        }
        return { type: 'count', value: raw };
      }
      if (name === 'AddOperators' && this.seatsExceeded) {
        return { type: 'warning', value: '!' };
      }
      if (badgeKey === 'companyProfile' && b.companyProfile) {
        return { type: 'danger', value: '!' };
      }
      return null;
    },
  },
  props: {
    link: {
      type: Object,
    }
  },
  watch: {
  },
  methods: {
    getMenuItemIcon(item, isActive) {
      return isActive ? item.renovaDashboardActiveIcon : item.renovaDashboardIcon;
    },
    logout() {
      this.$store.dispatch(AUTH_LOGOUT)
        .then(() => {
          this.$router.push('/login');
        });
    },
    linkAvailable(item) {
      // Priority: if route is gated by a feature flag, use feature availability first
      if (item.feature) {
        return this.featureAvailable(item.feature);
      }
      // Otherwise, if a locker text is provided, render the disabled state with locker tooltip
      if (item.featureLockerText) {
        return false;
      }
      return true;
    },
    reloadPage() {
      window.location.reload();
    },
    openLink() {
      if (this.link.externalLink) {
        window.open(this.link.externalLink, '_blank');
      }
      this.$emit('linkClick');
    }
  },
};
</script>

<style lang="scss" scoped>
.sidebar__link {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  gap: 8px;
  border-radius: 8px;
  position: relative;

  &--disable {
    cursor: default;

    p,
    .sidebar__link-icon {
      opacity: 0.4;
      pointer-events: none;
    }
  }

  p {
    text-overflow: ellipsis;
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    max-width: 160px;
  }

  &--bottom {
    margin-top: auto;
  }

  .sidebar__link-badge {
    min-width: 20px;
    height: 20px;
    border-radius: 10px;
    padding: 0 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    flex-shrink: 0;

    &--asap {
      background: var(--solid-red);
      color: #fff;
      margin-left: 4px;
    }

    &--dot {
      min-width: 8px;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      padding: 0;
      background-color: #6366f1;
    }

    &--count {
      background-color: #6366f1;
      color: #ffffff;
    }

    &--warning {
      background-color: #f59e0b;
      color: #ffffff;
    }

    &--danger {
      background-color: #ef4444;
      color: #ffffff;
    }
  }

  .sidebar__link-new {
    background: var(--solid-purple);
    border: 1px solid #D9D2FF;
    border-radius: 4px;
    padding: 2px 4px;
    font-weight: 600;
    font-size: 10px;
    line-height: 10px;
    color: #FFFFFF;
    margin-left: auto;
  }

  .sidebar__link-icon--active {
    display: none;
  }

  &.router-link-active {
    background: var(--solid-purple);

    p {
      color: white
    }

    .sidebar__link-new {
      background: white;
      color: var(--solid-purple);
      border-color: white;
    }

    .sidebar__link-icon {
      display: none;
    }

    .sidebar__link-icon--active {
      display: flex;
    }
  }

  img {
    width: 24px;
    height: 24px;
  }

  p {
    font-size: 14px;
    line-height: 16px;
    color: #D9D2FF;
  }
}

::v-deep .sidebar__link-locker {
  margin-left: auto;

  .feature-locker {
    position: relative;
    background-color: transparent !important;
  }
}
</style>
